import { Cell, Table as BaseTable } from '@/components_deprecated/Table'
import type { ApproveWorkPeriodNode } from './types.graphql'
import type { LeaderGroupNode } from '@/modules/companies'
import { useTranslate } from '@ur-solutions/react-hooks'
import { parseISO } from 'date-fns'
import { Checkmark } from '@/components_deprecated/Table/cells'
import { useUser } from '@/utils/hooks'
import { ApproveCommentModal } from './ApproveCommentModal'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import styled, { useTheme } from 'styled-components'
import { WorkPeriodDetail } from './WorkPeriodDetail'
import { combineDatesToReadableString } from '@/utils/date/combineDatesToReadableString'
import { getWeek } from '@/utils/date-fns/getWeek'
import { DietButton } from './components/DietButton'
import {
  useChangeDietPermission,
  useWorkPeriodApprovePermissions,
} from './hooks'
import { AddonCell } from '../activities/components/overview/AddonCell'
import { useWorkPeriodMigrations } from './hooks/useWorkPeriodMigrations'
import { useSalaryModuleConfiguration } from './hooks/useSalaryModuleConfiguration'
import { SalaryDayButton } from './components/SalaryDayButton'
import { Button, Combobox, H2, IconTooltip } from '@/components'
import { SalarySettingSelect } from '@/components_deprecated/Select/SalarySettingSelect'
import { useState } from 'react'
import SalaryGantt from '@/components/kibo-ui/gantt/salary-gantt'

const StyledCell = styled(Cell)<{ clickable?: boolean }>`
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
`

const Table = styled(BaseTable)`
  thead tr {
    border-bottom: 1rem solid ${props => props.theme.colors.white};
  }
`

const CommentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;
`

const CommentText = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.3;
`

const CommentCellInner = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 100%;
`

const GroupWrapper = styled.div`
  margin: 2rem 0;
`

const TableRow = styled.tr<{
  color: string | null
  disableHover?: boolean
}>`
  ${props =>
    props.color &&
    `
  background-color: ${
    props.disableHover ? props.theme.colors.gray11 : props.color
  };
  td {
    background-color: ${
      props.disableHover ? props.theme.colors.gray11 : props.color
    };
  }
  `};
  ${props =>
    props.disableHover
      ? ''
      : `
  &:hover {
    background-color: ${props.theme.colors.gray10};
    td {
      background-color: ${props.theme.colors.gray10};
    }
  }
  `};
`

const PlannedWork = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 16ch;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
`

const PlannedWorkAndIcon = styled.span`
  display: flex;
  align-items: center;
`

export const WorkPeriodApproveTable: React.FC<{
  workPeriods: ApproveWorkPeriodNode[]
  leaderGroups: LeaderGroupNode[]
  showUserColumn?: boolean
  loading: boolean
  initRefetch: () => void
}> = ({ workPeriods, leaderGroups, loading, showUserColumn, initRefetch }) => {
  const translations = useTranslate({
    user: 'common.user',
    date: 'common.date',
    approvedByUser: 'common.approved-by-driver',
    approvedByManager: 'common.approved-by-manager',
    comment: 'common.comment',
    managerComment: 'common.manager-comment',
    totalWork: 'common.total-work',
    weekN: ['common.week-n', { n: '0' }],
    diet: 'common.diet',
    salaryDay: 'common.salary-day',
    salaryMode: 'common.salary-mode',
    leaderGroup: 'common.leader-group',
    chooseGroup: 'common.choose-group',
    addons: 'salary.addons',
    transferedOvertimeToFlextime: 'salary.transfered-overtime-to-flextime',
    markIndividualActivitiesAsNotPaid:
      'salary.mark-individual-activities-as-not-paid',
    theSumShowsTotalWorkBeforeManuallyAddedHours:
      'salary.the-sum-shows-total-work-before-manually-added-hours',
    manualHours: 'salary.this-work-day-has-manual-hours-from-time-entries',
  })

  const me = useUser()
  const theme = useTheme()
  const addPrompt = usePrompt()
  const [openWorkPeriods, setOpenWorkPeriods] = useState<string[]>([])

  const salaryConfiguration = useSalaryModuleConfiguration()

  const { workPeriodApprovalState } = useWorkPeriodApprovePermissions()

  const { transferOvertimeToFlex, patchWorkPeriod } = useWorkPeriodMigrations()
  const canEditDiet = useChangeDietPermission()
  async function handleApproveWorkPeriodDriver(
    workPeriod: ApproveWorkPeriodNode
  ) {
    const { data } = await addPrompt<{
      comment: string
      approved: boolean
    } | null>(resolve => (
      <ApproveCommentModal
        existingComment={workPeriod.userComment}
        existingApproved={workPeriod.approvedByUser}
        onSubmit={resolve}
      />
    ))
    if (!data) return
    await patchWorkPeriod({
      variables: {
        id: workPeriod.id,
        input: {
          approvedByUser: data.approved,
          userComment: data.comment,
        },
      },
    })
    initRefetch()
  }

  async function handleApproveWorkPeriodManager(
    workPeriod: ApproveWorkPeriodNode
  ) {
    const { data } = await addPrompt<{
      comment: string
      approved: boolean
    } | null>(resolve => (
      <ApproveCommentModal
        existingComment={workPeriod.managerComment}
        existingApproved={workPeriod.approvedByManager}
        onSubmit={resolve}
      />
    ))
    if (!data) return
    await patchWorkPeriod({
      variables: {
        id: workPeriod.id,
        input: {
          approvedByManager: data.approved,
          managerComment: data.comment,
        },
      },
    })
    initRefetch()
  }

  async function showWorkPeriodDetails(workPeriod: ApproveWorkPeriodNode) {
    await addPrompt<{ id: string } | null>(resolve => (
      <WorkPeriodDetail workPeriodId={workPeriod.id} onSubmit={resolve} />
    ))
  }

  // Group work periods by week
  const workPeriodGroups = workPeriods.reduce(
    (acc, workPeriod) => {
      const weekStart = getWeek(parseISO(workPeriod.datetimeStart))

      if (!acc[weekStart]) {
        acc[weekStart] = []
      }
      acc[weekStart].push(workPeriod)

      return acc
    },
    {} as Record<string, ApproveWorkPeriodNode[]>
  )

  function computeColWidths(
    workPeriodGroup: ApproveWorkPeriodNode[],
    showUserColumn?: boolean
  ) {
    const hasUserComments = workPeriodGroup.some(wp => !!wp.userComment?.trim())
    const hasManagerComments = workPeriodGroup.some(
      wp => !!wp.managerComment?.trim()
    )
    // If there are no comments, keep those cols narrow; otherwise equal to PlannedWork.
    const userCommentColWidth = hasUserComments
      ? showUserColumn
        ? '25%'
        : '31%'
      : showUserColumn
      ? '12%'
      : '14%'

    const managerCommentColWidth = hasManagerComments
      ? showUserColumn
        ? '25%'
        : '31%'
      : showUserColumn
      ? '12%'
      : '14%'

    // If both comment columns are empty, give the space to the date column.
    const dateColWidth =
      !hasUserComments && !hasManagerComments
        ? showUserColumn
          ? '44%'
          : '48%'
        : showUserColumn
        ? '28%'
        : '30%'

    return { dateColWidth, userCommentColWidth, managerCommentColWidth }
  }

  return (
    <span>
      {Object.entries(workPeriodGroups).map(group => {
        const weekNumber = group[0]
        const workPeriodGroup = group[1]

        const { dateColWidth, userCommentColWidth, managerCommentColWidth } =
          computeColWidths(workPeriodGroup, showUserColumn)

        return (
          <GroupWrapper key={weekNumber + workPeriodGroup[0]?.id}>
            <H2>{translations.weekN({ n: weekNumber })}</H2>
            <Table
              noShadow
              noBorders
              noData={workPeriods.length === 0}
              loading={loading}
            >
              <colgroup>
                {showUserColumn && <col style={{ width: '14%' }} />}
                <col style={{ width: dateColWidth }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: userCommentColWidth }} />
                <col style={{ width: managerCommentColWidth }} />
              </colgroup>

              <thead>
                <tr>
                  {showUserColumn && <th colSpan={2}>{translations.user}</th>}
                  <th colSpan={showUserColumn ? 1 : 2}>{translations.date}</th>
                  <th>{translations.salaryMode}</th>
                  <th>{translations.leaderGroup}</th>
                  <th>{translations.diet}</th>
                  <th>{translations.addons}</th>
                  {salaryConfiguration.driverApprove && (
                    <th>{translations.approvedByUser}</th>
                  )}
                  {salaryConfiguration.managerApprove && (
                    <th>{translations.approvedByManager}</th>
                  )}
                  {salaryConfiguration.canTransferWorkToAbsence && (
                    <th>{translations.transferedOvertimeToFlextime}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {workPeriodGroup.map(workPeriod => {
                  const {
                    driverApproveDirty,
                    managerApproveDirty,
                    canApproveAsDriver,
                    canApproveAsManager,
                  } = workPeriodApprovalState(me.id, workPeriod)

                  let color: string | null = 'white'
                  if (
                    !workPeriod.approvedByUser &&
                    salaryConfiguration.driverApprove &&
                    workPeriod.userComment
                  ) {
                    color = theme.colors.red100
                  }
                  if (
                    !workPeriod.approvedByManager &&
                    salaryConfiguration.managerApprove &&
                    workPeriod.managerComment
                  ) {
                    color = theme.colors.red100
                  }

                  if (workPeriod.approvedByManager) {
                    color = theme.colors.green100
                  }

                  if (
                    !salaryConfiguration.driverApprove &&
                    !salaryConfiguration.managerApprove
                  ) {
                    color = null
                  }
                  const numColumns =
                    (showUserColumn ? 1 : 0) +
                    6 +
                    (salaryConfiguration.driverApprove ? 1 : 0) +
                    (salaryConfiguration.managerApprove ? 1 : 0) +
                    (salaryConfiguration.canTransferWorkToAbsence ? 1 : 0)
                  const isOpen = openWorkPeriods.includes(workPeriod.id)
                  const leaderGroupItems = [
                    ...leaderGroups.map(group => ({
                      value: group.id,
                      label: group.name,
                    })),
                  ]
                  if (
                    workPeriod.leaderGroup &&
                    !leaderGroupItems.some(
                      item => item.value === workPeriod.leaderGroup?.id
                    )
                  ) {
                    leaderGroupItems.unshift({
                      value: workPeriod.leaderGroup.id,
                      label: workPeriod.leaderGroup.name,
                    })
                  }

                  const workPeriodStart = parseISO(workPeriod.datetimeStart)
                  const workPeriodEnd = parseISO(workPeriod.datetimeEnd)

                  return (
                    <>
                      <TableRow
                        key={workPeriod.id}
                        color={color}
                        disableHover={isOpen}
                      >
                        <td>
                          {workPeriod.hasTimeEntries && (
                            <IconTooltip
                              triggerClassName="mr-2"
                              icon={{
                                icon: 'info-circle',
                                type: 'regular',
                                style: {
                                  color: 'red',
                                },
                              }}
                            >
                              {translations.manualHours}
                            </IconTooltip>
                          )}
                          {workPeriod.salarySetting && (
                            <Button
                              size="icon-lg"
                              variant="outline"
                              onClick={() => {
                                if (isOpen) {
                                  setOpenWorkPeriods(
                                    openWorkPeriods.filter(
                                      id => id !== workPeriod.id
                                    )
                                  )
                                } else {
                                  setOpenWorkPeriods([
                                    ...openWorkPeriods,
                                    workPeriod.id,
                                  ])
                                }
                              }}
                            >
                              <Icon
                                icon="truck"
                                color={theme.colors.primary}
                                size="30"
                              />
                            </Button>
                          )}
                        </td>
                        {showUserColumn && <td>{workPeriod.user.fullName}</td>}
                        <td width="1px">
                          <PlannedWorkAndIcon>
                            <PlannedWork>
                              <b>
                                {workPeriod.calendarItem &&
                                  combineDatesToReadableString(
                                    parseISO(workPeriod.calendarItem.start),
                                    parseISO(workPeriod.calendarItem.end),
                                    false
                                  )}
                              </b>
                              <span>
                                {combineDatesToReadableString(
                                  workPeriodStart,
                                  workPeriodEnd,
                                  false
                                )}
                              </span>
                            </PlannedWork>
                            <Icon
                              icon="circle-info"
                              type="solid"
                              size="28"
                              cursor="pointer"
                              margin="0 0 0 0.5rem"
                              color={theme.colors.secondary}
                              onClick={() => showWorkPeriodDetails(workPeriod)}
                            />
                          </PlannedWorkAndIcon>
                        </td>
                        <td width="1px">
                          <SalarySettingSelect
                            value={workPeriod.salarySetting?.id || null}
                            onChange={async value => {
                              await patchWorkPeriod({
                                variables: {
                                  id: workPeriod.id,
                                  input: {
                                    salarySetting: value === '' ? null : value,
                                  },
                                },
                              })
                              initRefetch()
                            }}
                          />
                          {workPeriod.salarySetting?.salaryType == 'DAY' && (
                            <SalaryDayButton
                              workPeriod={workPeriod}
                              salaryDay={workPeriod.salaryDay}
                              userId={workPeriod.user.id}
                              canEdit={
                                canApproveAsManager ||
                                (!workPeriod.approvedByManager &&
                                  !workPeriod.approvedByUser)
                              }
                              border={true}
                              onChange={() => initRefetch()}
                              restrictWidth
                            />
                          )}
                        </td>
                        <td width="1px">
                          {(leaderGroupItems.length > 0 ||
                            workPeriod.leaderGroup) && (
                            <Combobox
                              mode="single"
                              value={workPeriod.leaderGroup?.id ?? ''}
                              items={leaderGroupItems}
                              clearable
                              className="min-w-[180px]"
                              placeholder={translations.chooseGroup}
                              onChange={async value => {
                                await patchWorkPeriod({
                                  variables: {
                                    id: workPeriod.id,
                                    input: {
                                      leaderGroup: value || null,
                                    },
                                  },
                                })
                                initRefetch()
                              }}
                            />
                          )}
                        </td>
                        <td width="1px">
                          <DietButton
                            workPeriod={workPeriod}
                            userId={workPeriod.user.id}
                            canEdit={
                              canEditDiet &&
                              (canApproveAsManager ||
                                (!workPeriod.approvedByManager &&
                                  !workPeriod.approvedByUser))
                            }
                            border={true}
                            onChange={() => initRefetch()}
                          />
                        </td>
                        <AddonCell
                          userIdOverride={workPeriod.user.id}
                          report={{
                            addonsAsText: workPeriod.addonsAsText,
                            date: workPeriod.datetimeStart,
                          }}
                          datetimeStart={workPeriodStart}
                          datetimeEnd={workPeriodEnd}
                          subColumn={false}
                        />
                        {/* User comment / approval */}
                        {salaryConfiguration.driverApprove && (
                          <StyledCell
                            narrow
                            clickable={canApproveAsDriver}
                            onClick={() =>
                              canApproveAsDriver &&
                              handleApproveWorkPeriodDriver(workPeriod)
                            }
                          >
                            <CommentRow>
                              <Checkmark
                                checked={workPeriod.approvedByUser}
                                icon={
                                  !driverApproveDirty
                                    ? 'circle-plus'
                                    : undefined
                                }
                                color={
                                  !driverApproveDirty
                                    ? theme.colors.gray3
                                    : undefined
                                }
                              />
                              <CommentCellInner>
                                {workPeriod.userComment && (
                                  <CommentText>
                                    {workPeriod.userComment}
                                  </CommentText>
                                )}
                              </CommentCellInner>
                            </CommentRow>
                          </StyledCell>
                        )}

                        {/* Manager comment / approval */}
                        {salaryConfiguration.managerApprove && (
                          <StyledCell
                            clickable
                            narrow
                            onClick={() => {
                              if (!canApproveAsManager) return
                              void handleApproveWorkPeriodManager(workPeriod)
                            }}
                          >
                            <CommentRow>
                              <Checkmark
                                checked={workPeriod.approvedByManager}
                                icon={
                                  !managerApproveDirty
                                    ? 'circle-plus'
                                    : undefined
                                }
                                color={
                                  !managerApproveDirty
                                    ? theme.colors.gray3
                                    : undefined
                                }
                              />
                              <CommentCellInner>
                                {workPeriod.managerComment && (
                                  <CommentText>
                                    {workPeriod.managerComment}
                                  </CommentText>
                                )}
                              </CommentCellInner>
                            </CommentRow>
                          </StyledCell>
                        )}
                        {salaryConfiguration.canTransferWorkToAbsence && (
                          <StyledCell
                            clickable
                            narrow
                            centered
                            onClick={() => {
                              if (workPeriod.absenceHourTransaction) return
                              void transferOvertimeToFlex({
                                variables: { workPeriod: workPeriod.id },
                              })
                            }}
                          >
                            <Icon
                              icon={
                                workPeriod.absenceHourTransaction
                                  ? 'check'
                                  : 'times'
                              }
                              color={
                                workPeriod.absenceHourTransaction
                                  ? theme.colors.green
                                  : undefined
                              }
                            />
                          </StyledCell>
                        )}
                      </TableRow>
                      {workPeriod.salarySetting && isOpen && (
                        <TableRow
                          key={workPeriod.id + '-gantt'}
                          color={theme.colors.gray11}
                          disableHover={true}
                        >
                          <td colSpan={numColumns}>
                            <SalaryGantt
                              workPeriodId={workPeriod.id}
                              activitiesOnly={
                                workPeriod.salarySetting.salaryType !== 'HOUR'
                              }
                            />
                          </td>
                        </TableRow>
                      )}
                    </>
                  )
                })}
              </tbody>
            </Table>
          </GroupWrapper>
        )
      })}
    </span>
  )
}
